import { useChatInputStore } from '@/store';
import { getTemporaryFileId } from '@/constants/utils/files';

/**
 * Removes a temporary upload placeholder from every chat-input context.
 * Scoped removal is not enough here: the user can switch conversations while
 * an upload is in flight, and a leaked `isUploading` placeholder disables the
 * Send button until a full page reload.
 */
export const removeTempUploadPlaceholder = (tempId) => {
  if (!tempId) return;
  try {
    useChatInputStore.getState().removeFileByIdFromAllContexts?.(tempId);
  } catch (e) {
    console.warn('[uploadFileWithPlaceholder] Failed to remove temp upload:', e?.message);
  }
};

/**
 * Shared composer upload flow:
 *   1. Validate against the server-provided allowlist (when loaded).
 *   2. Push an `isUploading` placeholder so the UI shows progress immediately.
 *   3. Upload; on success the caller's `onAddFile(uploaded, tempId)` swaps the
 *      placeholder for the real file.
 *   4. On ANY failure (exception or empty response) the placeholder is removed
 *      again, so the composer can never wedge in a permanent "uploading" state.
 *
 * All collaborator functions are passed in by the caller so this stays usable
 * from components, hooks, and non-React code alike.
 *
 * `extraTempFields` may be an object or a `(file) => object` factory for
 * per-file placeholder fields (e.g. `previewFile`).
 *
 * @returns {Promise<object|null>} the uploaded file on success, null otherwise
 */
export async function uploadFileWithPlaceholder({
  file,
  collectionId = null,
  uploadFile,
  isFileAllowed,
  addToast,
  onAddUploadingFile,
  onAddFile,
  t,
  extraTempFields,
}) {
  // `t` is optional — a few older call sites use hardcoded English strings.
  const translate = (key, fallback) => (typeof t === 'function' ? t(key) : fallback) || fallback;
  let tempId = null;
  try {
    const { isAllowed, message } = isFileAllowed(file);
    if (!isAllowed) {
      addToast(message, 'error');
      return null;
    }

    const extraFields = typeof extraTempFields === 'function'
      ? extraTempFields(file)
      : (extraTempFields || {});

    tempId = getTemporaryFileId(file.name);
    const tempFile = {
      id: tempId,
      name: file.name,
      size: file.size,
      type: file.type,
      isUploading: true,
      ...(file.uri ? { uri: file.uri } : {}),
      ...extraFields,
    };

    onAddUploadingFile?.(tempFile);

    const uploadedFile = await uploadFile({ file, name: file.name, collectionId });
    if (uploadedFile) {
      // Server files must never inherit a local placeholder's upload flag.
      // Normalizing here also makes the Send gate deterministic on native.
      const completedUpload = {
        ...uploadedFile,
        isUploading: false,
      };

      // Replace the placeholder in its original composer even if the user
      // switched conversations while the request was in flight. Callers only
      // receive completion metadata after a successful swap (for polling and
      // editor side effects); they must never perform a second attachment add
      // on their own. A placeholder that vanished mid-flight was removed by
      // the user, which cancels the attachment: re-adding the completed file
      // would land it in whatever context the store now points at (ghost
      // attachment in the switched-to conversation) or resurrect it in the
      // composer it was deleted from, so the result is dropped instead.
      const placeholderReplaced = useChatInputStore
        .getState()
        .replaceFileByIdInAllContexts?.(tempId, completedUpload) === true;
      if (placeholderReplaced) {
        onAddFile?.(completedUpload, tempId, { placeholderReplaced: true });
      }

      // Defensive cleanup for Android modal/context races: if replacement
      // happened in a different context (or the callback added the real file
      // separately), no `uploading_*` entry may survive to disable Send.
      removeTempUploadPlaceholder(tempId);
      // A cancelled upload (placeholder removed by the user) resolves to null
      // so batch callers and attachment lists never treat a dropped file as
      // an active attachment.
      return placeholderReplaced ? completedUpload : null;
    }

    removeTempUploadPlaceholder(tempId);
    addToast(translate('chatInput.uploadNoFileReturned', 'Upload succeeded but returned no file'), 'error');
    return null;
  } catch (error) {
    console.error('[uploadFileWithPlaceholder] Upload failed:', error);
    removeTempUploadPlaceholder(tempId);
    addToast(error?.message || translate('chatInput.uploadFailed', 'Failed to upload file'), 'error');
    return null;
  }
}

/**
 * Uploads several files in parallel, each with its own placeholder lifecycle.
 * Sequential `for await` uploads made multi-file picks feel broken (one slow
 * file blocked the rest) and amplified the stuck-placeholder bug.
 *
 * @returns {Promise<object[]>} the successfully uploaded files
 */
export async function uploadFilesWithPlaceholders(files, sharedArgs) {
  const results = await Promise.all(
    files.map((file) => uploadFileWithPlaceholder({ ...sharedArgs, file }))
  );
  return results.filter(Boolean);
}
